// src/pages/CandidateDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Breadcrumbs,
  Link,
  Tooltip,
  IconButton,
  Snackbar,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import HomeIcon from "@mui/icons-material/Home";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import MuiAlert from "@mui/material/Alert";
import { fetchCandidates } from "../api/tnsdc";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Styled candidate card
const CandidateCard = styled(Card)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  },
  height: "100%",
}));

const CandidateDetails = () => {
  const { batchId } = useParams();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [filterText, setFilterText] = useState("");
  const [sortOrder, setSortOrder] = useState("asc"); // asc or desc

  useEffect(() => {
    const getCandidates = async () => {
      try {
        const res = await fetchCandidates(batchId);
        const candidateArray =
          res.data.result?.GetBatchCandidateDetails?.CandidateArray || [];
        setCandidates(candidateArray);
      } catch (err) {
        console.error("Error fetching candidates:", err);
        alert("Failed to fetch candidates.");
      } finally {
        setLoading(false);
      }
    };

    getCandidates();
  }, [batchId]);

  // Download Excel
  const handleDownloadExcel = () => {
    if (!candidates.length) return;

    const data = candidates.map((c) => ({
      "Candidate Name": c.CandidateName,
      "Candidate ID": c.CandidateId,
      Email: c.CandidateEmail || "",
      Aadhaar: c.AadhaarNumberMasked || "",
      Attendance: c.AttendancePercentage || "",
      Status: c.CandidateStatus || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `Batch_${batchId}_Candidates.xlsx`);
  };

  // Copy candidate details
  const handleCopyCandidate = (candidate) => {
    const textToCopy = `
Candidate Name: ${candidate.CandidateName}
ID: ${candidate.CandidateId}
Email: ${candidate.CandidateEmail || "-"}
Aadhaar: ${candidate.AadhaarNumberMasked || "-"}
Attendance: ${candidate.AttendancePercentage || "-"}%
Status: ${candidate.CandidateStatus || "-"}
    `.trim();

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setSnackbarMessage("Candidate details copied!");
        setSnackbarOpen(true);
      })
      .catch((err) => console.error("Failed to copy: ", err));
  };

  // Filter and sort candidates
  const filteredCandidates = candidates
    .filter((c) =>
      c.CandidateName.toLowerCase().includes(filterText.toLowerCase())
    )
    .sort((a, b) =>
      sortOrder === "asc"
        ? a.CandidateName.localeCompare(b.CandidateName)
        : b.CandidateName.localeCompare(a.CandidateName)
    );

  return (
    <Box sx={{ p: 3, maxWidth: 1300, mx: "auto", minHeight: "100vh" }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        aria-label="breadcrumb"
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <Link
          component={RouterLink}
          to="/"
          color="inherit"
          underline="hover"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <HomeIcon sx={{ mr: 1 }} fontSize="small" />
          Dashboard
        </Link>
        <Typography color="text.primary">Candidate Details</Typography>
      </Breadcrumbs>

      {/* Header and Download */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={500}>
          Candidate Details
        </Typography>
        <Tooltip title="Download Excel" arrow>
          <span>
            <IconButton
              color="primary"
              onClick={handleDownloadExcel}
              disabled={!candidates.length}
              aria-label="Download Excel"
            >
              <DownloadIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Filter and Sort Controls */}
      <Box display="flex" justifyContent="space-between" mb={2} flexWrap="wrap" gap={2}>
        <TextField
          label="Search by Name"
          variant="outlined"
          size="small"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />

        <FormControl size="small">
          <InputLabel>Sort</InputLabel>
          <Select
            value={sortOrder}
            label="Sort"
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <MenuItem value="asc">A → Z</MenuItem>
            <MenuItem value="desc">Z → A</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Candidate Cards */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : filteredCandidates.length === 0 ? (
        <Typography>No candidates found.</Typography>
      ) : (
        <Grid container spacing={3}>
          {filteredCandidates.map((c) => (
            <Grid item xs={12} sm={6} md={4} key={c.CandidateId}>
              <CandidateCard>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {c.CandidateName}
                    </Typography>
                    <Tooltip title="Copy Details" arrow>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleCopyCandidate(c)}
                        aria-label="Copy candidate details"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    ID: {c.CandidateId}
                  </Typography>
                  {c.CandidateEmail && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Email: {c.CandidateEmail}
                    </Typography>
                  )}
                  {c.AadhaarNumberMasked && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Aadhaar: {c.AadhaarNumberMasked}
                    </Typography>
                  )}
                  {c.AttendancePercentage && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Attendance: {c.AttendancePercentage}%
                    </Typography>
                  )}
                  {c.CandidateStatus && (
                    <Box mt={1}>
                      <Chip
                        label={c.CandidateStatus}
                        color={
                          c.CandidateStatus === "Enroled"
                            ? "primary"
                            : c.CandidateStatus === "Completed"
                            ? "success"
                            : "default"
                        }
                        size="small"
                      />
                    </Box>
                  )}
                </CardContent>
              </CandidateCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default CandidateDetails;
